import { useEffect, useState, type ReactNode } from "react";
import { PageShell } from "../components/PageShell";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import {
  getLiftToPlay,
  getThemePref,
  getVolume,
  setLiftToPlay,
  setThemePref,
  setVolume,
  type ThemePref,
} from "../settings/prefs";
import { isSoundOn, playSfx, setSoundOn, isUiSoundOn, setUiSoundOn } from "../sound/sfx";
import { useSession } from "../store/context";

/** Stitch pill switch — a `role="switch"` button (accessible + testable),
 * mirroring the Profile/GameSettings toggles. Disabled renders a dimmed, inert
 * control. */
function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  disabled?: boolean;
}): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={disabled ? undefined : onChange}
      className={`relative h-6 w-11 shrink-0 appearance-none border-0 rounded-full transition-colors ${
        disabled ? "cursor-default opacity-50" : "cursor-pointer"
      } ${checked ? "bg-gold-deep" : "bg-surface-variant"}`}
    >
      <span
        aria-hidden
        className="absolute top-0.5 h-5 w-5 rounded-full bg-on-surface shadow"
        style={{ insetInlineStart: checked ? 22 : 2, transition: "inset-inline-start .2s ease" }}
      />
    </button>
  );
}

/** Per-row setting icons — the same gold-badge idiom as `ScreenTitle`, sized for
 * list rows. Decorative (`aria-hidden`); labels stay the accessible names. */
type SettingIconName =
  "sound" | "volume" | "bell" | "cards" | "theme" | "history" | "stats" | "blocked";

const SETTING_ICON_PATHS: Record<SettingIconName, string> = {
  sound:
    "M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z",
  volume: "M7 18h2V6H7v12zm4 4h2V2h-2v20zm-8-8h2v-4H3v4zm12 4h2V6h-2v12zm4-8v4h2v-4h-2z",
  bell: "M12 22a2.2 2.2 0 0 0 2.2-2.2H9.8A2.2 2.2 0 0 0 12 22zm6-5.8V11c0-3.07-1.64-5.64-4.5-6.32V4a1.5 1.5 0 0 0-3 0v.68C7.63 5.36 6 7.92 6 11v5.2L4.2 18v.9h15.6V18L18 16.2z",
  // Fanned cards — the same glyph as the waiting room's «طرح کارت» row.
  cards:
    "M2.53 19.65l1.34.56v-9.03l-2.43 5.86c-.41 1.02.08 2.19 1.09 2.61zm19.5-3.7L17.07 3.98c-.31-.75-1.04-1.21-1.81-1.23-.26 0-.53.04-.79.15L7.1 5.95c-.75.31-1.21 1.03-1.23 1.8-.01.27.04.54.15.8l4.96 11.97c.31.76 1.05 1.22 1.83 1.23.26 0 .52-.05.77-.15l7.36-3.05c1.02-.42 1.51-1.59 1.09-2.6zM7.88 8.75c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z",
  theme:
    "M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z",
  history:
    "M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z",
  stats: "M5 9.2h3V19H5V9.2zM10.6 5h2.8v14h-2.8V5zm5.6 8H19v6h-2.8v-6z",
  // Slashed circle — the same block glyph as the screen title and the DM header.
  blocked:
    "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9A7.94 7.94 0 0 1 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1A7.94 7.94 0 0 1 20 12c0 4.42-3.58 8-8 8z",
};

function RowIcon({ name }: { name: SettingIconName }): ReactNode {
  return (
    <span
      aria-hidden="true"
      className="flex h-8 w-8 flex-none items-center justify-center rounded-lg border border-gold-deep/25 bg-secondary-container text-on-secondary-container shadow-sm"
    >
      <svg viewBox="0 0 24 24" width={18} height={18} fill="currentColor" aria-hidden="true">
        <path d={SETTING_ICON_PATHS[name]} />
      </svg>
    </span>
  );
}

/** A felt settings card matching the design's blurred low-surface `section`. */
function Card({ children }: { children: ReactNode }): ReactNode {
  return (
    <section className="overflow-hidden rounded-xl border border-outline-variant/30 bg-surface-container-low/80 shadow-lg backdrop-blur-md">
      {children}
    </section>
  );
}

/** A group heading above a card. */
function GroupLabel({ children }: { children: ReactNode }): ReactNode {
  return (
    <h2 className="m-0 px-sm font-label-md text-label-md text-on-surface-variant">{children}</h2>
  );
}

/** «پوسته» options — auto (follow Telegram) / dark / light. */
const THEMES: readonly { value: ThemePref; label: string }[] = [
  { value: "auto", label: fa.settings.themeAuto },
  { value: "dark", label: fa.settings.themeDark },
  { value: "light", label: fa.settings.themeLight },
];

export function Settings(): ReactNode {
  const [sound, setSound] = useState(isSoundOn());
  const [uiSound, setUiSound] = useState(isUiSoundOn());
  const [volume, setVol] = useState(getVolume());
  // «پوسته» — persisted (`bing:theme`) and applied live to <html data-theme>.
  const [theme, setTheme] = useState<ThemePref>(getThemePref());
  // «تأیید قبل از بازی کارت» — persisted (`bing:liftToPlay`); حکم and بی‌دل read it when they render.
  const [liftToPlay, setLift] = useState(getLiftToPlay());
  // Profile PRIVACY toggles (server-persisted via saveProfile). Optimistic: the switch
  // flips at once and reverts if the save fails; it re-syncs whenever the profile changes.
  const profile = useSession((s) => s.profile);
  const saveProfile = useSession((s) => s.saveProfile);
  const updateNotifications = useSession((s) => s.updateNotifications);
  const setView = useSession((s) => s.setView);
  const pageReturn = useSession((s) => s.pageReturn);
  // Push-notification toggle (moved here from Profile) — optimistic, reverts on failure.
  const [notifications, setNotifications] = useState(profile?.notificationsEnabled ?? true);
  useEffect(() => {
    if (profile) setNotifications(profile.notificationsEnabled);
  }, [profile?.notificationsEnabled]);
  const [hideHistory, setHideHistory] = useState(profile?.hideHistory ?? false);
  const [hideStats, setHideStats] = useState(profile?.hideStats ?? false);
  useEffect(() => {
    if (!profile) return;
    setHideHistory(profile.hideHistory);
    setHideStats(profile.hideStats);
  }, [profile?.hideHistory, profile?.hideStats]);
  async function togglePrivacy(
    field: "hideHistory" | "hideStats",
    current: boolean,
    apply: (v: boolean) => void,
  ): Promise<void> {
    const next = !current;
    apply(next); // optimistic
    try {
      await saveProfile({ [field]: next });
    } catch {
      apply(current); // revert on failure
    }
  }

  const volumePct = Math.round(volume * 100);

  return (
    <PageShell title={fa.settings.title} icon="settings" onBack={() => setView(pageReturn)}>
      {/* Audio group — sound on/off + master volume. */}
      <div className="flex flex-col gap-sm">
        <GroupLabel>{fa.settings.audioSection}</GroupLabel>
        <Card>
          <div className="flex items-center justify-between gap-md p-md border-b border-outline-variant/30">
            <div className="flex min-w-0 items-center gap-2.5">
              <RowIcon name="sound" />
              <div className="flex min-w-0 flex-col">
                <span className="font-body-lg text-body-lg text-on-surface">
                  {fa.settings.sound}
                </span>
                <span className="font-caption text-caption text-on-surface-variant">
                  {fa.settings.soundHint}
                </span>
              </div>
            </div>
            <Toggle
              checked={sound}
              label={fa.settings.sound}
              onChange={() => {
                const next = !sound;
                setSoundOn(next);
                setSound(next);
                if (next) playSfx("play"); // a card tick — a sample of what game sound IS
              }}
            />
          </div>
          <div className="flex items-center justify-between gap-md p-md border-b border-outline-variant/30">
            <div className="flex min-w-0 items-center gap-2.5">
              <RowIcon name="sound" />
              <div className="flex min-w-0 flex-col">
                <span className="font-body-lg text-body-lg text-on-surface">
                  {fa.settings.uiSound}
                </span>
                <span className="font-caption text-caption text-on-surface-variant">
                  {fa.settings.uiSoundHint}
                </span>
              </div>
            </div>
            <Toggle
              checked={uiSound}
              label={fa.settings.uiSound}
              onChange={() => {
                const next = !uiSound;
                setUiSoundOn(next);
                setUiSound(next);
                if (next) playSfx("notify");
              }}
            />
          </div>
          <div className="flex items-center gap-md p-md">
            <span className="flex items-center gap-2.5">
              <RowIcon name="volume" />
              <span className="font-body-lg text-body-lg text-on-surface">
                {fa.settings.volume}
              </span>
            </span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={volumePct}
              aria-label={fa.settings.volume}
              onChange={(e) => {
                const next = Number(e.target.value) / 100;
                setVol(next);
                setVolume(next);
              }}
              // `playSfx` already honours the right toggle for the clip (a tap is a UI cue),
              // so gating it on the GAME switch here just made the preview inconsistent
              // with what the slider is about to change.
              onPointerUp={() => playSfx("tap")}
              className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-surface-variant accent-gold-deep"
            />
            <span
              className="w-10 text-left font-body-md text-body-md text-on-surface-variant"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {faDigits(volumePct)}٪
            </span>
          </div>
        </Card>
      </div>

      {/* Notifications — Telegram push for friend requests / game invites (moved here
          from Profile). Optimistic, server-persisted; reverts on failure. */}
      <div className="flex flex-col gap-sm">
        <GroupLabel>{fa.settings.notificationsSection}</GroupLabel>
        <Card>
          <div className="flex items-center justify-between gap-md p-md">
            <div className="flex min-w-0 items-center gap-2.5">
              <RowIcon name="bell" />
              <div className="flex min-w-0 flex-col">
                <span className="font-body-lg text-body-lg text-on-surface">
                  {fa.settings.notifications}
                </span>
                <span className="font-caption text-caption text-on-surface-variant">
                  {fa.settings.notificationsHint}
                </span>
              </div>
            </div>
            <Toggle
              checked={notifications}
              label={fa.settings.notifications}
              onChange={() => {
                const next = !notifications;
                setNotifications(next); // optimistic
                void updateNotifications(next).catch(() => setNotifications(!next));
              }}
            />
          </div>
        </Card>
      </div>

      {/* Gameplay group — «تأیید قبل از بازی کارت» (operator, 2026-10-08: players sometimes play a
          card by mistake). ON: a tap on a card in حکم / بی‌دل only lifts it and a tap on the table
          plays it (`game/lift-to-play.ts`). OFF by default; persisted in `bing:liftToPlay`. */}
      <div className="flex flex-col gap-sm">
        <GroupLabel>{fa.settings.gameplaySection}</GroupLabel>
        <Card>
          <div className="flex items-center justify-between gap-md p-md">
            <div className="flex min-w-0 items-center gap-2.5">
              <RowIcon name="cards" />
              <div className="flex min-w-0 flex-col">
                <span className="font-body-lg text-body-lg text-on-surface">
                  {fa.settings.liftToPlay}
                </span>
                <span className="font-caption text-caption text-on-surface-variant">
                  {fa.settings.liftToPlayHint}
                </span>
              </div>
            </div>
            <Toggle
              checked={liftToPlay}
              label={fa.settings.liftToPlay}
              onChange={() => {
                const next = !liftToPlay;
                setLiftToPlay(next);
                setLift(next);
              }}
            />
          </div>
        </Card>
      </div>

      {/* Display group — the «پوسته» theme (خودکار follows Telegram's scheme;
          the choice persists in `bing:theme` and applies live via <html
          data-theme>, which the Telegram header color also follows). */}
      <div className="flex flex-col gap-sm">
        <GroupLabel>{fa.settings.displaySection}</GroupLabel>
        <Card>
          <div className="flex flex-col gap-sm p-md">
            <div className="flex min-w-0 items-center gap-2.5">
              <RowIcon name="theme" />
              <div className="flex min-w-0 flex-col">
                <span className="font-body-lg text-body-lg text-on-surface">
                  {fa.settings.theme}
                </span>
                <span className="font-caption text-caption text-on-surface-variant">
                  {fa.settings.themeHint}
                </span>
              </div>
            </div>
            <div
              role="radiogroup"
              aria-label={fa.settings.theme}
              className="flex gap-1 rounded-lg bg-surface-container-highest p-1"
            >
              {THEMES.map((opt) => {
                const on = theme === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-label={opt.label}
                    onClick={() => {
                      setTheme(opt.value);
                      setThemePref(opt.value);
                    }}
                    className={`flex-1 appearance-none cursor-pointer whitespace-nowrap rounded-md border-0 py-2 text-center font-body-md text-body-md transition-colors ${
                      on
                        ? "bg-gold-deep text-ink-on-gold shadow"
                        : "bg-transparent text-on-surface-variant hover:text-on-surface"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </Card>
      </div>

      {/* Privacy group — hide match history / stats+trophies from OTHER users' profile
          view. Persisted server-side (saveProfile); the profile-view endpoint enforces it. */}
      <div className="flex flex-col gap-sm">
        <GroupLabel>{fa.settings.privacySection}</GroupLabel>
        <Card>
          <div className="flex items-center justify-between gap-md p-md border-b border-outline-variant/30">
            <div className="flex min-w-0 items-center gap-2.5">
              <RowIcon name="history" />
              <div className="flex min-w-0 flex-col">
                <span className="font-body-lg text-body-lg text-on-surface">
                  {fa.settings.hideHistory}
                </span>
                <span className="font-caption text-caption text-on-surface-variant">
                  {fa.settings.hideHistoryHint}
                </span>
              </div>
            </div>
            <Toggle
              checked={hideHistory}
              label={fa.settings.hideHistory}
              onChange={() => void togglePrivacy("hideHistory", hideHistory, setHideHistory)}
            />
          </div>
          <div className="flex items-center justify-between gap-md p-md border-b border-outline-variant/30">
            <div className="flex min-w-0 items-center gap-2.5">
              <RowIcon name="stats" />
              <div className="flex min-w-0 flex-col">
                <span className="font-body-lg text-body-lg text-on-surface">
                  {fa.settings.hideStats}
                </span>
                <span className="font-caption text-caption text-on-surface-variant">
                  {fa.settings.hideStatsHint}
                </span>
              </div>
            </div>
            <Toggle
              checked={hideStats}
              label={fa.settings.hideStats}
              onChange={() => void togglePrivacy("hideStats", hideStats, setHideStats)}
            />
          </div>
          {/* «کاربران مسدود» — ALWAYS rendered, whether or not anything is blocked. A row
              that appears only when it has content cannot answer «where did the people I
              blocked go?», which is the whole reason the screen exists; the empty list says
              so in words instead. No count is shown, because knowing it would mean fetching
              the list on every settings open for a number nobody needs. */}
          <button
            type="button"
            onClick={() => setView("blockedUsers")}
            className="flex w-full appearance-none items-center justify-between gap-md border-0 bg-transparent p-md text-right cursor-pointer"
          >
            <span className="flex min-w-0 items-center gap-2.5">
              <RowIcon name="blocked" />
              <span className="flex min-w-0 flex-col">
                <span className="font-body-lg text-body-lg text-on-surface">
                  {fa.settings.blocked}
                </span>
                <span className="font-caption text-caption text-on-surface-variant">
                  {fa.settings.blockedHint}
                </span>
              </span>
            </span>
            {/* Chevron pointing INTO the page — right-to-left, so it points left. */}
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              width={20}
              height={20}
              fill="currentColor"
              className="flex-none text-on-surface-variant"
            >
              <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>
        </Card>
      </div>
    </PageShell>
  );
}
